// Demo store for invoices, contacts, VAT codes and invoice designs. It follows
// the ledger's own rules so the screens behave as they will against the API:
// VAT per line rounded half away from zero, numbers only given at posting
// (no gaps), posted documents never edited, void keeps the number.
import { cleanPeople } from './contactPeople';
import { ZERO_ID } from './source';
import { dueDateFor } from '../screens/invoices/shared';
import { addMoney, compareMoney, isZero, mulMoney, negate, percentOf } from '@elysian/ui';
import { demoTaxCodes, demoVatRate } from './mockVat';
import type {
  Account, AccountBalance, Contact, DocumentInput, DocumentSummary, DocumentTemplate, EmailMessage, LedgerDocument,
  LedgerSource, TemplateSettings, ContactInput, CashDocument, RecordCashInput,
  ImportMapping, InvoiceImportCheck, InvoiceImportInput, DocumentList, DocumentListQuery,
} from './source';

type DocsSource = Pick<LedgerSource, 'recordReceipt' | 'recordPayment' | 'onAccount' | 'cashDocument' | 'reallocateCash' | 'undoCash' | 'refundOnAccount' | 'contactTransactions' | 'importContacts' |
  'accounts' | 'taxCodes' | 'contacts' | 'documents' | 'document' | 'createDraft' | 'replaceDraft' | 'deleteDraft'
  | 'postDocument' | 'voidDocument' | 'creditAndReissue' | 'sendDocument' | 'templates' | 'saveTemplate' | 'createTemplate' | 'updateTemplate' | 'deleteTemplate' | 'pdf'
  | 'contact' | 'createContact' | 'updateContact' | 'allocateCredit' | 'trialBalance' | 'balanceSheet' | 'agedCreditors'
  | 'checkInvoiceImport' | 'invoiceMappings' | 'saveInvoiceMapping' | 'deleteInvoiceMapping' | 'documentList'>;

const delay = <T,>(v: T, ms = 80): Promise<T> => new Promise((r) => setTimeout(() => r(structuredClone(v)), ms));
const fail = (detail: string) => Promise.reject(new Error(detail));
const DEMO_TODAY = '2026-10-12';
/** Contacts brought in by an import, by the package's own id (Sage's Unique ID). */
const importedIds = new Map<string, string>();

const ACCOUNTS: Account[] = [
  { id: 'a4000', code: '4000', name: 'Sales — goods', class: 'income', control: 'none', is_active: true, default_tax_code: 'S23' },
  { id: 'a4010', code: '4010', name: 'Sales — services', class: 'income', control: 'none', is_active: true, default_tax_code: 'R135' },
  { id: 'a4020', code: '4020', name: 'Sales — maintenance contracts', class: 'income', control: 'none', is_active: true, default_tax_code: 'R135' },
  { id: 'a5000', code: '5000', name: 'Materials purchased', class: 'expense', control: 'none', is_active: true, default_tax_code: 'S23' },
  { id: 'a5100', code: '5100', name: 'Subcontractors', class: 'expense', control: 'none', is_active: true, default_tax_code: 'RCT' },
  { id: 'a6000', code: '6000', name: 'Wages and salaries', class: 'expense', control: 'none', is_active: true },
  { id: 'a7000', code: '7000', name: 'Rent and rates', class: 'expense', control: 'none', is_active: true, default_tax_code: 'EX' },
  { id: 'a7100', code: '7100', name: 'Light and heat', class: 'expense', control: 'none', is_active: true, default_tax_code: 'R135' },
  { id: 'a7200', code: '7200', name: 'Motor expenses', class: 'expense', control: 'none', is_active: true, default_tax_code: 'S23' },
  { id: 'a7300', code: '7300', name: 'Telephone and internet', class: 'expense', control: 'none', is_active: true, default_tax_code: 'S23' },
  { id: 'a7400', code: '7400', name: 'Software and subscriptions', class: 'expense', control: 'none', is_active: true, default_tax_code: 'S23' },
  { id: 'a7600', code: '7600', name: 'Accountancy and legal', class: 'expense', control: 'none', is_active: true, default_tax_code: 'S23' },
  { id: 'a7800', code: '7800', name: 'Depreciation', class: 'expense', control: 'none', is_active: true },
  { id: 'a7900', code: '7900', name: 'Bank charges', class: 'expense', control: 'none', is_active: true, default_tax_code: 'EX' },
  { id: 'a7950', code: '7950', name: 'Exchange gains and losses', class: 'expense', control: 'fx_gain_loss', is_active: true },
  { id: 'a4900', code: '4900', name: 'Other income', class: 'income', control: 'none', is_active: false, default_tax_code: 'EX' },
  { id: 'a0030', code: '0030', name: 'Equipment and vehicles', class: 'asset', control: 'none', is_active: true },
  { id: 'a0031', code: '0031', name: 'Equipment — depreciation to date', class: 'asset', control: 'none', is_active: true },
  { id: 'a1100', code: '1100', name: 'Debtors control', class: 'asset', control: 'debtors', is_active: true },
  { id: 'a1230', code: '1230', name: 'Petty cash', class: 'asset', control: 'none', is_active: true },
  { id: 'a2100', code: '2100', name: 'Creditors control', class: 'liability', control: 'creditors', is_active: true },
  { id: 'a2200', code: '2200', name: 'VAT on sales', class: 'liability', control: 'vat', is_active: true },
  { id: 'a2210', code: '2210', name: 'PAYE / PRSI / USC', class: 'liability', control: 'none', is_active: true },
  { id: 'a2109', code: '2109', name: 'Accruals', class: 'liability', control: 'none', is_active: true },
  { id: 'a3000', code: '3000', name: 'Capital introduced', class: 'equity', control: 'none', is_active: true },
  { id: 'a3200', code: '3200', name: 'Retained earnings', class: 'equity', control: 'retained_earnings', is_active: true },
  { id: 'a9999', code: '9999', name: 'Suspense — needs an account', class: 'asset', control: 'suspense', is_active: true },
  { id: 'a1200', code: '1200', name: 'AIB Current', class: 'asset', control: 'bank', is_active: true },
  { id: 'a1210', code: '1210', name: 'AIB Deposit', class: 'asset', control: 'bank', is_active: true },
  { id: 'a1220', code: '1220', name: 'Wise GBP', class: 'asset', control: 'bank', currency: 'GBP', is_active: true },
];


const customer = (id: string, name: string, email: string, terms: number, extra: Partial<Contact> = {}): Contact => ({
  id, name, email, is_customer: true, is_supplier: false, is_active: true, balance: '0', payment_terms_days: terms, country: 'IE', ...extra,
});

const CONTACTS: Contact[] = [
  customer('c1', 'Swords Pavilions', 'accounts@swordspavilions.ie', 30, { vat_number: 'IE9876543W', address: { line1: 'Main Street', town: 'Swords', county: 'Co. Dublin', postcode: 'K67 X2Y4' } }),
  customer('c2', 'Parkwest Business Campus', 'ap@parkwest.ie', 30, { address: { line1: 'Nangor Road', town: 'Dublin 12', postcode: 'D12 R2K8' } }),
  customer('c3', 'Ballymun Credit Union', 'office@ballymuncu.ie', 14),
  customer('c4', 'Dublin Hotels Group', 'finance@dublinhotels.ie', 30, { vat_number: 'IE3344556K' }),
  customer('c5', 'Sandyford Retail Ltd', 'invoices@sandyfordretail.ie', 30),
  customer('c6', 'Naas Road Logistics', 'ap@naasroadlogistics.ie', 30),
  customer('c7', 'Tallaght Medical Centre', 'practice@tallaghtmc.ie', 30),
  customer('c8', 'Belfast Security Supplies', 'accounts@bss.co.uk', 30, { country: 'GB', currency: 'GBP', address: { line1: '14 Duncrue Street', town: 'Belfast', postcode: 'BT3 9AQ' } }),
  supplier('s1', 'Detector Wholesale Ltd', 'accounts@dw.ie', 30, 'IE9876543W'),
  supplier('s2', 'Eir Business', 'billing@eir.ie', 14),
  supplier('s3', 'Electric Ireland', 'business@electricireland.ie', 14, 'IE8F52100V'),
  supplier('s4', 'Circle K Fuel Card', 'fuelcards@circlek.ie', 30),
  supplier('s5', 'Screwfix Ireland', 'trade@screwfix.ie', 30),
  { ...supplier('s6', 'Northline Detection Inc', 'ar@northline-detection.com', 30), country: 'US', currency: 'USD' },
  { ...supplier('s7', 'Shenzhen Anxin Sensor Co., Ltd', 'sales@anxin-sensor.cn', 30), country: 'CN', currency: 'CNY' },
];

function supplier(id: string, name: string, email: string, terms: number, vat_number?: string): Contact {
  return { id, name, email, vat_number, is_customer: false, is_supplier: true, is_active: true, balance: '0', payment_terms_days: terms, country: 'IE' };
}

interface Stored extends Omit<LedgerDocument, 'base_gross_total' | 'base_outstanding'> { payments: { date: string; amount: string; cash?: string }[] }
/** Receipts and payments entered by hand (#26), as ledger.record_cash keeps them. */
let cash: CashDocument[] = [];
let cashSeq = 0;
const onAccountOf = (c: CashDocument) => (c.status === 'void' || c.is_refund ? '0.0000' : addMoney(c.amount, negate(c.allocated)));

/** As the summaries view: the euro value at the document's own rate. */
function baseOf(v: string, rate: string | undefined) {
  return (Math.round(Number(v) * Number(rate ?? '1') * 100) / 100).toFixed(2);
}
function withBase<T extends { gross_total: string; outstanding: string; fx_rate?: string }>(d: T) {
  return { ...d, base_gross_total: baseOf(d.gross_total, d.fx_rate), base_outstanding: baseOf(d.outstanding, d.fx_rate) };
}

let docs: Stored[] = [];
let templates: DocumentTemplate[] = [];
type Counter = { prefix: string; next_value: number; digits: number };
let numbering: Record<string, Counter> = {};
let seq = 0;

const formatNumber = (c: Counter, n = c.next_value) => `${c.prefix}${String(n).padStart(c.digits, '0')}`;
/** The next number for a kind of document, as ledger.next_number gives it. */
function takeNumber(type: string): string {
  const c = numbering[type]!;
  const no = formatNumber(c);
  c.next_value += 1;
  return no;
}
/** The demo's numbering for documents (Settings → Organisation, #13). */
export const demoDocumentNumbering = () => numbering;
/** The numbers already used, for one kind, with a prefix. */
export const demoNumbersUsed = (type: string, prefix: string) =>
  docs.filter((d) => d.type === type && d.doc_no?.startsWith(prefix) && /^\d{1,18}$/.test(d.doc_no.slice(prefix.length)))
    .map((d) => Number(d.doc_no!.slice(prefix.length)));

function rateOf(code: string | undefined, on?: string) {
  return demoVatRate(code, on);
}

/** The same sums ledger.save_draft does. */
export function workOut(input: DocumentInput) {
  const lines = input.lines.map((l, i) => {
    const net = mulMoney(l.quantity ?? '1', l.unit_price);
    const tax = l.tax_amount ?? percentOf(net, rateOf(l.tax_code, input.issue_date));
    return { ...l, quantity: l.quantity ?? '1', line_no: i + 1, net_amount: net, tax_amount: tax };
  });
  const net = addMoney('0', ...lines.map((l) => l.net_amount));
  const tax = addMoney('0', ...lines.map((l) => l.tax_amount));
  return { lines, net, tax, gross: addMoney(net, tax) };
}

/** Set by the demo source: the rate ledger.rate_for would give (base per unit), or null. */
let rateLookup: (currency: string, date: string) => string | null = () => null;
export function setRateLookup(fn: (currency: string, date: string) => string | null) {
  rateLookup = fn;
}

function build(id: string, input: DocumentInput, prev?: Stored): Stored {
  const c = CONTACTS.find((x) => x.id === input.contact_id);
  const t = workOut(input);
  const currency = input.currency ?? 'EUR';
  // As ledger.trg_document_fx: a foreign draft without a rate takes the day's rate.
  const fx_rate = currency === 'EUR' ? '1' : input.fx_rate ?? rateLookup(currency, input.issue_date) ?? '1';
  return {
    id,
    type: input.type,
    doc_no: prev?.doc_no ?? null,
    status: 'draft',
    contact_id: input.contact_id,
    contact_name: c?.name ?? '',
    issue_date: input.issue_date,
    due_date: input.due_date ?? (input.contact_id && input.issue_date ? dueDateFor(input.issue_date, CONTACTS.find((c) => c.id === input.contact_id)) : null),
    supplier_ref: input.supplier_ref ?? null,
    currency,
    fx_rate,
    reference: input.reference ?? null,
    notes: input.notes ?? null,
    net_total: t.net,
    tax_total: t.tax,
    gross_total: t.gross,
    outstanding: t.gross,
    lines: t.lines,
    allocations: [],
    emails: [],
    payments: [],
    template_version: null,
    ...(prev?.import_doc_no ? { import_doc_no: prev.import_doc_no } : {}),
  };
}

function settle(d: Stored): Stored {
  if (d.status === 'draft' || d.status === 'void') return d;
  const paid = addMoney('0', ...d.payments.map((p) => p.amount));
  const outstanding = addMoney(d.gross_total, negate(paid));
  const status = isZero(outstanding) || compareMoney(outstanding, '0') < 0 ? 'paid' : isZero(paid) ? 'posted' : 'part_paid';
  return { ...d, outstanding: compareMoney(outstanding, '0') < 0 ? '0.0000' : outstanding, status };
}

function summary(d: Stored): DocumentSummary {
  const { lines: _l, allocations: _a, emails: _e, payments: _p, reference: _r, notes: _n, journal_id: _j, template_version: _t, attachments: _x, fx_rate: _f, ...s } = d;
  return { ...s, send_status: d.emails?.at(-1)?.status ?? null, base_gross_total: baseOf(d.gross_total, d.fx_rate), base_outstanding: baseOf(d.outstanding, d.fx_rate) };
}

const line = (description: string, quantity: string, unit_price: string, account_code: string, tax_code: string) =>
  ({ description, quantity, unit_price, account_code, tax_code });

function seed() {
  docs = [];
  numbering = {
    sales_invoice: { prefix: 'INV-', next_value: 1041, digits: 4 }, sales_credit: { prefix: 'CN-', next_value: 13, digits: 1 },
    purchase_bill: { prefix: 'BILL-', next_value: 311, digits: 1 }, purchase_credit: { prefix: 'SCN-', next_value: 9, digits: 1 },
  };
  seq = 0;
  const add = (input: DocumentInput, opts: { post?: boolean; paid?: { date: string; amount: string }[]; sent?: boolean } = {}) => {
    const d = build(`d${++seq}`, input);
    if (opts.post) {
      d.doc_no = takeNumber(input.type);
      d.status = 'posted';
      d.template_version = 3;
      d.payments = opts.paid ?? [];
      d.allocations = d.payments.map((p) => ({ document_id: 'bank', doc_no: 'AIB receipt', amount: p.amount, date: p.date }));
      if (opts.sent) d.emails = [{ id: `e${seq}`, status: 'delivered', to: [CONTACTS.find((c) => c.id === input.contact_id)!.email!], subject: `Invoice ${d.doc_no}`, created_at: `${input.issue_date}T09:12:00Z` }];
    }
    docs.push(settle(d));
  };
  const inv = (contact_id: string, issue_date: string, terms: number, lines: DocumentInput['lines'], reference?: string): DocumentInput => {
    const due = new Date(`${issue_date}T12:00:00Z`);
    due.setUTCDate(due.getUTCDate() + terms);
    return { type: 'sales_invoice', contact_id, issue_date, due_date: due.toISOString().slice(0, 10), reference, lines };
  };
  add(inv('c7', '2026-06-10', 30, [line('Emergency lighting annual test and certificate', '1', '272.69', '4020', 'R135')]), { post: true, sent: true });
  add(inv('c5', '2026-07-21', 30, [line('Fire alarm panel replacement — Unit 4', '1', '722.47', '4010', 'R135')], 'PO 7712'), { post: true, sent: true });
  add(inv('c4', '2026-08-18', 30, [line('Quarterly fire alarm service — 3 hotels', '3', '264.32', '4020', 'R135')], 'DHG-PO-2231'), { post: true, sent: true });
  add(inv('c6', '2026-08-25', 30, [line('Call-out: fault on zone 7', '1', '165.00', '4010', 'R135'), line('Optical smoke detectors', '6', '44.85', '4000', 'S23')]), { post: true, sent: true, paid: [{ date: '2026-09-30', amount: '100.00' }] });
  add(inv('c2', '2026-09-08', 30, [line('Access control upgrade — Block C', '1', '1980.00', '4010', 'R135'), line('Paxton Net2 controllers', '2', '186.00', '4000', 'S23')], 'PW-0915'), { post: true, sent: true, paid: [{ date: '2026-10-06', amount: '1000.00' }] });
  add(inv('c3', '2026-09-28', 14, [line('CCTV maintenance contract — Q4', '1', '2466.96', '4020', 'R135')]), { post: true, sent: true });
  add(inv('c1', '2026-10-02', 30, [line('Fire alarm installation — Pavilion East, stage 2', '1', '3200.00', '4010', 'R135'), line('Addressable sounders', '10', '42.50', '4000', 'S23')], 'SP-PO-5521'), { post: true, sent: true });
  add(inv('c4', '2026-09-01', 30, [line('Fire door survey — 28 doors', '1', '980.00', '4010', 'R135')]), { post: true, sent: true, paid: [{ date: '2026-09-25', amount: '1112.30' }] });
  add(inv('c1', '2026-10-09', 30, [line('Monitoring contract — November', '1', '85.00', '4020', 'R135')], 'SP-PO-5530'));
  // Supplier bills already approved, and a credit note not yet set against an invoice.
  add({ type: 'purchase_bill', contact_id: 's1', issue_date: '2026-09-02', due_date: '2026-10-02', supplier_ref: 'DW-88412',
    lines: [line('Detectors and bases', '1', '1840.00', '5000', 'S23')] }, { post: true, paid: [{ date: '2026-10-01', amount: '2263.20' }] });
  add({ type: 'purchase_bill', contact_id: 's2', issue_date: '2026-09-26', due_date: '2026-10-10', supplier_ref: 'EIR-0926',
    lines: [line('Broadband and mobiles — September', '1', '142.28', '7300', 'S23')] }, { post: true });
  add({ type: 'purchase_bill', contact_id: 's1', issue_date: '2026-10-01', due_date: '2026-10-31', supplier_ref: 'DW-88907',
    lines: [line('Paxton Net2 controllers', '6', '121.00', '5000', 'S23')] }, { post: true });
  add({ type: 'sales_credit', contact_id: 'c4', issue_date: '2026-10-06', reference: 'DHG-PO-2231', notes: 'One site was not visited in the August service.',
    lines: [line('Credit: quarterly service, Ballsbridge not visited', '1', '100.00', '4020', 'R135')] }, { post: true });
  // Sterling work for a Northern Ireland customer, and a bill in dollars.
  add({ ...inv('c8', '2026-08-27', 30, [line('Aspirating smoke detection — warehouse survey', '1', '1850.00', '4010', 'Z')], 'BSS-4471'), currency: 'GBP', fx_rate: '1.172470' },
    { post: true, sent: true });
  add({ type: 'purchase_bill', contact_id: 's6', issue_date: '2026-09-15', due_date: '2026-10-15', supplier_ref: 'NL-20931', currency: 'USD', fx_rate: '0.889680',
    lines: [line('Beam detectors — 8 units', '8', '300.00', '5000', 'Z')] }, { post: true });

  templates = [{
    id: 't1', kind: 'sales_invoice', name: 'Standard', layout: 'classic', is_default: true, version: 3,
    settings: {
      title: 'Invoice',
      page: { size: 'A4', margin_mm: 18 },
      brand: { accent: '#0F6E56', font: 'dm_sans', logo_position: 'left', logo_height_mm: 18 },
      fields: { customer_vat_number: true, customer_reference: true, due_date: true, line_vat_amount: false, bank_details: true, notes: true, contact_email: true },
      blocks: [{ type: 'terms', zone: 'footer', text: 'Payment due by {{invoice.due_date}}. Please quote {{invoice.number}} with your payment.' }],
    },
  }];
}
seed();

/** Shared with the demo inbox and bank, which work on the same documents. */
export const demoStore = {
  get: (id: string) => docs.find((d) => d.id === id),
  all: () => docs,
  open: () => docs.filter((d) => (d.status === 'posted' || d.status === 'part_paid') && compareMoney(d.outstanding, '0') > 0),
  createDraft(input: DocumentInput): string {
    const d = build(`d${++seq}`, input);
    docs.push(d);
    return d.id;
  },
  post(id: string): Stored {
    const d = docs.find((x) => x.id === id)!;
    const posted = settle({ ...d, status: 'posted', doc_no: takeNumber(d.type) });
    docs = docs.map((x) => (x.id === id ? posted : x));
    return posted;
  },
  remove(id: string) {
    docs = docs.filter((x) => x.id !== id);
  },
  pay(id: string, date: string, amount: string) {
    docs = docs.map((x) => (x.id === id ? settle({ ...x, payments: [...x.payments, { date, amount }], allocations: [...(x.allocations ?? []), { document_id: 'bank', doc_no: 'Bank', amount, date }] }) : x));
  },
  unpay(id: string, amount: string) {
    docs = docs.map((x) => {
      if (x.id !== id) return x;
      const i = x.payments.findIndex((p) => p.amount === amount);
      const payments = x.payments.filter((_, j) => j !== i);
      const allocations = (x.allocations ?? []).filter((_, j) => j !== i);
      return settle({ ...x, status: 'posted', payments, allocations });
    });
  },
  usesSuspense: (id: string) => docs.find((d) => d.id === id)?.lines.some((l) => l.account_code === '9999') ?? false,
  accounts: ACCOUNTS,
  contacts: CONTACTS,
  addContact(c: Contact) {
    CONTACTS.push(c);
  },
};

/** The saved designs, for checks that cross demo stores. */
export function currentTemplates(): DocumentTemplate[] {
  return templates;
}

/** Tests start from the same demo data each time. */
export function resetDemoDocuments() {
  seed();
  cash = [];
  cashSeq = 0;
  importedIds.clear();
  mappings = [];
}

/** Saved invoice CSV mappings (#41), as import_mappings. */
let mappings: ImportMapping[] = [];
const INVOICE_FIELDS = ['type', 'customer_code', 'customer_name', 'number', 'date', 'due_date', 'order_no', 'description',
  'nominal_code', 'vat_code', 'vat_rate', 'net', 'vat', 'gross', 'currency'];
const money2 = (n: number) => (Math.round(n * 100 + Math.sign(n) * 1e-9) / 100);
const roundHalfAway = (n: number) => Math.sign(n) * Math.round(Math.abs(n) * 100 + 1e-9) / 100;

/** As ledger.check_invoice_import (db/048): each line checked, grouped by number, drafts made with apply. */
function checkInvoiceImport(input: InvoiceImportInput): Promise<InvoiceImportCheck> {
  // As db/049 (#48): the mapping's values for the Type column; any other type is a problem on its line.
  const inv = (input.type_values?.invoice ?? []).map((v) => v.trim().toUpperCase()).filter(Boolean);
  const cred = (input.type_values?.credit ?? []).map((v) => v.trim().toUpperCase()).filter(Boolean);
  if (inv.length || cred.length) {
    if (inv.some((v) => cred.includes(v))) return fail('The same value can’t mean both an invoice and a credit note');
    const unknown = new Map<number, string>();
    const lines = input.lines.map((l) => {
      const t = (l.type ?? '').trim().toUpperCase();
      if (!t) { const { type: _t, ...rest } = l; return rest; }
      if (inv.includes(t)) return { ...l, type: 'Invoice' };
      if (cred.includes(t)) return { ...l, type: 'Credit note' };
      unknown.set(l.row_no, l.type!.trim());
      return { ...l, type: 'Invoice' };
    });
    if (!unknown.size) return checkInvoiceImport({ ...input, type_values: undefined, lines });
    if (input.apply) return fail(`Nothing imported: ${unknown.size} line${unknown.size === 1 ? '' : 's'} with a type that’s neither an invoice nor a credit note`);
    return checkInvoiceImport({ ...input, type_values: undefined, lines }).then((r) => {
      const rows = r.rows.map((x) => (unknown.has(x.row_no)
        ? { ...x, status: 'problem' as const, problems: [`Type “${unknown.get(x.row_no)}” is neither an invoice (${inv.join(', ') || 'none set'}) nor a credit note (${cred.join(', ') || 'none set'})`, ...(x.status === 'skip' ? [] : x.problems)] }
        : x));
      return { ...r, rows, counts: { ...r.counts, problems: rows.filter((x) => x.status === 'problem').length, skipped: rows.filter((x) => x.status === 'skip').length },
        documents: r.documents.map((d) => (rows.some((x) => x.number === d.number && x.type === d.type && x.status === 'problem') ? { ...d, status: 'problem' as const } : d)) };
    });
  }
  type Row = InvoiceImportCheck['rows'][number] & { customer_id?: string; due_date?: string; order_no?: string; currency: string };
  const codes = demoTaxCodes();
  const salesCodes = codes.filter((c) => c.applies_to !== 'purchases');
  const ORDER = ['S23', 'R135', 'R9', 'L48', 'Z', 'EX', 'OS'];
  const rows: Row[] = input.lines.map((l) => {
    const problems: string[] = [];
    const num = (v?: string | null) => (v == null || v.trim() === '' ? null : Number(v));
    let net = num(l.net); let vat = num(l.vat); let gross = num(l.gross); let rate = num(l.vat_rate);
    if ([net, vat, gross, rate].some((x) => x !== null && Number.isNaN(x))) { problems.push('An amount isn’t a number'); net = vat = gross = rate = null; }
    if (net === null && gross !== null && vat !== null) net = gross - vat;
    const typeWord = (l.type ?? '').toLowerCase();
    const type: 'sales_invoice' | 'sales_credit' = /(credit|^cr|^crn|^sc$|^c$|refund)/.test(typeWord) ? 'sales_credit'
      : typeWord ? 'sales_invoice' : (net ?? gross ?? 0) < 0 ? 'sales_credit' : 'sales_invoice';
    net = net === null ? null : Math.abs(net); vat = vat === null ? null : Math.abs(vat); gross = gross === null ? null : Math.abs(gross);
    const number = l.number?.trim() || null;
    if (!number) problems.push('No invoice number');
    const iso = /^\d{4}-\d{2}-\d{2}$/;
    if ((l.date && !iso.test(l.date)) || (l.due_date && !iso.test(l.due_date))) problems.push('A date isn’t one the Ledger can read');
    else if (!l.date) problems.push('No date');
    if (net === null) problems.push('No net amount (or gross and VAT)');
    const code = l.customer_code?.trim() ?? '';
    const cust = code ? CONTACTS.find((c) => c.is_customer && (c.code ?? '').toUpperCase() === code.toUpperCase()) : undefined;
    if (!code) problems.push('No account code for the customer');
    const acc = l.nominal_code ? ACCOUNTS.find((a) => a.code === l.nominal_code!.trim()) : undefined;
    if (!l.nominal_code?.trim()) problems.push('No nominal code');
    else if (!acc) problems.push(`No account ${l.nominal_code.trim()} in the chart of accounts`);
    else if (acc.is_active === false) problems.push(`Account ${acc.code} is switched off`);
    else if (['debtors', 'creditors', 'bank', 'vat', 'opening_balance'].includes(acc.control)) problems.push(`Account ${acc.code} is a control account and can’t go on an invoice line`);
    if (rate !== null && rate > 0 && rate < 1) rate *= 100;
    if (rate === null && !l.vat_code && vat !== null && net) {
      const best = salesCodes.filter((c) => Math.abs(roundHalfAway(net! * Number(c.rate) / 100) - vat!) <= 0.02)
        .sort((a, b) => Math.abs(roundHalfAway(net! * Number(a.rate) / 100) - vat!) - Math.abs(roundHalfAway(net! * Number(b.rate) / 100) - vat!) || Number(b.rate) - Number(a.rate))[0];
      if (best) rate = Number(best.rate);
    }
    const tc = l.vat_code ? salesCodes.find((c) => c.code.toUpperCase() === l.vat_code!.trim().toUpperCase())
      : rate !== null ? salesCodes.filter((c) => ORDER.includes(c.code) && Number(c.rate) === rate).sort((a, b) => ORDER.indexOf(a.code) - ORDER.indexOf(b.code))[0] : undefined;
    if (!tc && l.date) problems.push(l.vat_code ? `No sales VAT code ${l.vat_code.trim()}` : rate !== null ? `No sales VAT code at ${rate}%` : 'No VAT rate (or VAT amount) to choose a VAT code from');
    let calc: number | null = null;
    if (tc && net !== null) {
      calc = tc.is_reverse_charge ? 0 : roundHalfAway(net * Number(tc.rate) / 100);
      if (vat !== null && Math.abs(calc - vat) > 0.02) problems.push(`VAT in the file is ${vat.toFixed(2)}, but ${net.toFixed(2)} at ${tc.code} is ${calc.toFixed(2)}`);
      if (gross !== null && Math.abs(net + (vat ?? calc) - gross) > 0.01) problems.push(`Net and VAT come to ${(net + (vat ?? calc)).toFixed(2)}, not the gross ${gross.toFixed(2)}`);
    }
    return {
      row_no: l.row_no, status: problems.length ? 'problem' : code && !cust ? 'missing_customer' : 'ok', problems, type, number,
      customer: cust?.name ?? l.customer_name ?? null, customer_code: code || null, customer_id: cust?.id, date: l.date ?? null,
      account_code: acc?.code ?? null, tax_code: tc?.code ?? null, net: net === null ? null : money2(net), vat: calc,
      description: l.description?.trim() || acc?.name || 'Imported line', due_date: l.due_date ?? undefined, order_no: l.order_no ?? undefined,
      currency: (l.currency ?? cust?.currency ?? 'EUR').toUpperCase(),
    };
  });
  for (const r of rows) {
    if (r.number && rows.some((o) => o !== r && o.number === r.number && o.type === r.type && (o.customer_code !== r.customer_code || o.date !== r.date || o.currency !== r.currency))) {
      r.status = 'problem'; r.problems.push('Other lines with this number have a different customer, date, currency or invoice/credit type');
    }
    if ((r.status === 'ok' || r.status === 'missing_customer') && docs.some((d) => d.type === r.type && (d.doc_no === r.number || (d.status === 'draft' && d.import_doc_no === r.number)))) {
      r.status = 'skip'; r.problems = ['Already in the Ledger'];
    }
    const seq = numbering[r.type];
    const digits = r.number && seq && r.number.startsWith(seq.prefix) ? r.number.slice(seq.prefix.length) : '';
    if ((r.status === 'ok' || r.status === 'missing_customer') && /^\d{1,18}$/.test(digits) && digits.length >= seq!.digits
        && (digits.length === seq!.digits || digits[0] !== '0') && Number(digits) >= seq!.next_value) {
      r.status = 'problem';
      r.problems.push(`${r.number} looks like one of your own numbers (${seq!.prefix ? `${seq!.prefix}…` : 'numbers only'}): the Ledger would reach it later. Change the prefix under Settings → Organisation → Document numbers first`);
    }
  }
  const problems = rows.filter((r) => r.status === 'problem').length;
  const missingMap = new Map<string, { code: string; name: string | null; rows: number[] }>();
  for (const r of rows.filter((x) => !x.customer_id && x.customer_code && x.status !== 'skip')) {
    const m = missingMap.get(r.customer_code!) ?? { code: r.customer_code!, name: r.customer ?? null, rows: [] as number[] };
    m.rows.push(r.row_no); missingMap.set(r.customer_code!, m);
  }
  const missing = [...missingMap.values()].sort((a, b) => a.code.localeCompare(b.code));
  const groups = new Map<string, Row[]>();
  for (const r of rows.filter((x) => x.number)) groups.set(`${r.type}|${r.number}`, [...(groups.get(`${r.type}|${r.number}`) ?? []), r]);
  const documents: InvoiceImportCheck['documents'] = [...groups.values()].map((g) => {
    const net = g.reduce((t, r) => t + (r.net ?? 0), 0); const vat = g.reduce((t, r) => t + (r.vat ?? 0), 0);
    return { number: g[0]!.number!, type: g[0]!.type, customer: g[0]!.customer, customer_code: g[0]!.customer_code, date: g[0]!.date, currency: g[0]!.currency,
      lines: g.length, net: money2(net), vat: money2(vat), gross: money2(net + vat),
      status: (g.some((r) => r.status === 'problem') ? 'problem' : g.some((r) => r.status === 'missing_customer') ? 'missing_customer' : g.every((r) => r.status === 'skip') ? 'skip' : 'ok') as InvoiceImportCheck['documents'][number]['status'] };
  }).sort((a, b) => (a.date ?? '').localeCompare(b.date ?? '') || a.number.localeCompare(b.number));
  let imported = 0;
  if (input.apply) {
    if (missing.length) return fail(`Nothing imported: ${missing.length} customer${missing.length === 1 ? ' is' : 's are'} not in the Ledger (${missing.map((m) => m.code).join(', ')}). Add them, or import them first`);
    if (problems) return fail(`Nothing imported: ${problems} line${problems === 1 ? ' needs' : 's need'} attention`);
    for (const g of [...groups.values()].filter((x) => x.every((r) => r.status === 'ok'))) {
      const f = g[0]!;
      const d = build(`d${++seq}`, {
        type: f.type, contact_id: f.customer_id!, issue_date: f.date!, due_date: g.find((r) => r.due_date)?.due_date, currency: f.currency,
        reference: g.find((r) => r.order_no)?.order_no,
        lines: g.map((r) => ({ description: r.description!, quantity: '1', unit_price: String(r.net), account_code: r.account_code!, tax_code: r.tax_code! })),
      });
      docs.push({ ...d, import_doc_no: f.number! });
      imported++;
    }
  }
  const signed = (r: Row, v: number | null | undefined) => (r.type === 'sales_credit' ? -(v ?? 0) : (v ?? 0));
  const live = rows.filter((r) => r.status !== 'skip');
  return delay({
    rows: rows.map(({ customer_id: _c, due_date: _d, order_no: _o, currency: _x, ...r }) => r), documents, missing_customers: missing,
    counts: { lines: rows.length, problems, skipped: rows.filter((r) => r.status === 'skip').length, missing_customers: missing.length,
      invoices: documents.filter((d) => d.type === 'sales_invoice' && d.status !== 'skip').length,
      credit_notes: documents.filter((d) => d.type === 'sales_credit' && d.status !== 'skip').length },
    totals: { net: money2(live.reduce((t, r) => t + signed(r, r.net), 0)), vat: money2(live.reduce((t, r) => t + signed(r, r.vat), 0)) },
    imported,
  });
}

function requireDraft(id: string): Stored | Promise<never> {
  const d = docs.find((x) => x.id === id);
  if (!d) return fail('That document no longer exists.') as Promise<never>;
  if (d.status !== 'draft') return fail('Posted documents can’t be changed. Void it and raise a new one.') as Promise<never>;
  return d;
}

function validate(input: DocumentInput): string | null {
  if (!CONTACTS.some((c) => c.id === input.contact_id)) return 'Choose a customer.';
  if (!input.lines.length) return 'Add at least one line.';
  for (const l of input.lines) {
    if (!l.description.trim()) return 'Every line needs a description.';
    const a = ACCOUNTS.find((x) => x.code === l.account_code);
    if (!a) return `Account ${l.account_code} doesn’t exist.`;
    if (a.control !== 'none' && a.control !== 'suspense') return `Account ${a.code} is a control account; it can’t be used on a line.`;
  }
  return null;
}

function allocateCash(c: CashDocument, allocations: { document_id: string; amount: string }[]): Promise<void> {
  const against = c.type === 'receipt' ? 'sales_invoice' : 'purchase_bill';
  let left = onAccountOf(c);
  for (const a of allocations) {
    const inv = docs.find((x) => x.id === a.document_id);
    if (!inv || inv.type !== against || inv.contact_id !== c.contact_id || (inv.status !== 'posted' && inv.status !== 'part_paid')) {
      return fail(`Only ${c.contact_name}’s posted ${c.type === 'receipt' ? 'invoices' : 'bills'} can be paid here`);
    }
    if (compareMoney(a.amount, '0') <= 0) return fail('Allocations are positive amounts');
    if (compareMoney(a.amount, inv.outstanding) > 0) return fail(`${inv.doc_no} has only ${inv.outstanding.replace(/(\.\d\d)00$/, '$1')} left to pay`);
    left = addMoney(left, negate(a.amount));
  }
  if (compareMoney(left, '0') < 0) return fail(`The allocations are more than the amount (${c.amount.replace(/(\.\d\d)00$/, '$1')})`);
  for (const a of allocations) {
    docs = docs.map((x) => (x.id === a.document_id
      ? settle({ ...x, payments: [...x.payments, { date: c.date, amount: a.amount, cash: c.id }],
          allocations: [...(x.allocations ?? []), { document_id: c.id, doc_no: c.number, amount: a.amount, date: c.date }] })
      : x));
    c.allocations.push({ document_id: a.document_id, number: docs.find((d) => d.id === a.document_id)?.doc_no ?? null, amount: a.amount });
  }
  c.allocated = addMoney(c.allocated, ...allocations.map((a) => a.amount));
  c.on_account = onAccountOf(c);
  return Promise.resolve();
}

/** Take part (or all) of what a receipt pays off an invoice, as a negative allocation does (db/038). */
function takeOffCash(c: CashDocument, docId: string, amount: string, date: string) {
  const net = (list: { amount: string }[]) => addMoney('0', ...list.map((x) => x.amount));
  docs = docs.map((x) => {
    if (x.id !== docId) return x;
    const mine = x.payments.filter((p) => p.cash === c.id);
    const left = addMoney(net(mine), negate(amount));
    const payments = [...x.payments.filter((p) => p.cash !== c.id), ...(isZero(left) ? [] : [{ date, amount: left, cash: c.id }])];
    const allocs = (x.allocations ?? []).filter((a) => a.document_id === c.id);
    const aLeft = addMoney(net(allocs), negate(amount));
    const allocations = [...(x.allocations ?? []).filter((a) => a.document_id !== c.id), ...(isZero(aLeft) ? [] : [{ document_id: c.id, doc_no: c.number, amount: aLeft, date }])];
    return settle({ ...x, payments, allocations });
  });
  c.allocations = c.allocations.flatMap((a) => {
    if (a.document_id !== docId) return [a];
    const left = addMoney(a.amount, negate(amount));
    return isZero(left) ? [] : [{ ...a, amount: left }];
  });
  c.allocated = addMoney(c.allocated, negate(amount));
  c.on_account = onAccountOf(c);
}

async function recordCash(type: 'receipt' | 'payment', input: RecordCashInput): Promise<CashDocument> {
  const c = CONTACTS.find((x) => x.id === input.contact_id);
  if (!c) return fail('That customer or supplier isn’t in these books');
  if (type === 'receipt' && !c.is_customer) return fail(`${c.name} isn’t a customer`);
  if (type === 'payment' && !c.is_supplier) return fail(`${c.name} isn’t a supplier`);
  if (!/^\d+(\.\d{1,2})?$/.test(String(input.amount).trim()) || Number(input.amount) <= 0) return fail(`Enter the amount ${type === 'receipt' ? 'received' : 'paid'}, more than nothing`);
  const bank = demoStore.accounts.find((a) => a.code === input.bank_account && a.control === 'bank');
  if (!bank) return fail(`Choose the bank account it was ${type === 'receipt' ? 'paid into' : 'paid from'} (how it was paid)`);
  if (bank.is_active === false) return fail(`${bank.name} is inactive; make it active under Bank accounts first`);
  const amount = Number(input.amount).toFixed(4);
  const doc: CashDocument = {
    id: `cash${++cashSeq}`, type, number: `${type === 'receipt' ? 'RCT' : 'PAY'}-${String(cashSeq).padStart(6, '0')}`,
    contact_id: c.id, contact_name: c.name, date: input.date ?? DEMO_TODAY, amount, currency: 'EUR',
    bank_account: { code: bank.code, name: bank.name }, reference: input.reference ?? null, notes: input.notes ?? null,
    allocated: '0.0000', on_account: amount, allocations: [], in_bank: false, status: 'posted', is_refund: false, refunds: null, voided: null,
  };
  const allocs = (input.allocations ?? []).filter((a) => Number(a.amount) > 0);
  if (allocs.length) {
    const check = structuredClone(doc);
    try { await allocateCash(check, []); } catch { /* nothing to check yet */ }
    const total = allocs.reduce((t, a) => t + Number(a.amount), 0);
    if (total > Number(amount) + 1e-9) return fail(`The allocations (${total.toFixed(2)}) are more than the amount (${Number(amount).toFixed(2)})`);
    await allocateCash(doc, allocs);
  }
  cash.push(doc);
  return delay(doc);
}

/** For the demo bank reconciliation: money entered by hand into an account, not yet matched to the statement. */
export const demoCashInBank = (account: string, amount: string) =>
  cash.filter((c) => !c.in_bank && c.status !== 'void' && c.bank_account?.code === account
    && compareMoney(c.type === 'receipt' ? c.amount : negate(c.amount), amount) === 0);
export const demoMarkCashInBank = (id: string) => { const c = cash.find((x) => x.id === id); if (c) c.in_bank = true; };
export const demoRecordCash = recordCash;
/** Receipts and payments entered against a bank account, for its transactions (#51). */
export const demoCashOnAccount = (code: string) => cash.filter((c) => c.bank_account?.code === code && c.status !== 'void');

/** As db/042 (#30): people tidied and checked, statements to those ticked, blank notes none. */
function withPeople(input: ContactInput): { fields: Partial<ContactInput> } | { problem: string } {
  const fields: Partial<ContactInput> = {};
  if (input.people) {
    const r = cleanPeople(input.people);
    if ('problem' in r) return r;
    fields.people = r.people;
    fields.statement_emails = r.people.filter((p) => p.gets_statements && p.email).map((p) => p.email!);
  }
  if (input.notes !== undefined) fields.notes = input.notes?.trim() || null;
  return { fields };
}

function cashThisYear(contactId: string): { paid_this_year: string; received_this_year: string } {
  const year = DEMO_DAY.slice(0, 4);
  const mine = cash.filter((x) => x.contact_id === contactId && x.status !== 'void' && x.date.startsWith(year));
  const sum = (pick: (x: CashDocument) => boolean, back: (x: CashDocument) => boolean) =>
    addMoney('0', ...mine.filter(pick).map((x) => x.amount), ...mine.filter(back).map((x) => negate(x.amount)));
  return {
    paid_this_year: sum((x) => x.type === 'payment' && !x.is_refund, (x) => x.type === 'receipt' && !!x.is_refund),
    received_this_year: sum((x) => x.type === 'receipt' && !x.is_refund, (x) => x.type === 'payment' && !!x.is_refund),
  };
}

export const demoDocuments: DocsSource = {
  accounts: (cls) => delay(ACCOUNTS.filter((a) => !cls || a.class === cls)),
  taxCodes: (on) => delay(demoTaxCodes(on)),
  contacts: (role) =>
    delay(CONTACTS.filter((c) => (role === 'customer' ? c.is_customer : c.is_supplier)).map((c) => {
      const open = docs.filter((d) => d.contact_id === c.id && d.type.startsWith(role === 'customer' ? 'sales' : 'purchase') && d.status !== 'draft' && d.status !== 'void');
      const held = cash.filter((x) => x.contact_id === c.id && x.type === (role === 'customer' ? 'receipt' : 'payment')).map(onAccountOf);
      return { ...c, balance: addMoney('0', ...open.map((d) => (d.type.endsWith('credit') ? negate(d.outstanding) : d.outstanding)), ...held.map(negate)),
        overdue: addMoney('0', ...open.filter((d) => !d.type.endsWith('credit') && d.due_date && d.due_date < DEMO_DAY).map((d) => d.outstanding)),
        // As ledger.contact_cash_this_year (db/044, #34): this calendar year, refunds taken off, undone ones left out.
        ...cashThisYear(c.id) };
    })),
  // As ledger.document_list (db/053, #49, #50): filtered, sorted, counted and paged over everything.
  documentList: (q: DocumentListQuery) => {
    const today = DEMO_DAY; // the demo's today, as the old list used the session's
    const sales = q.side === 'sales';
    const tab = q.tab ?? (sales ? 'all' : 'unpaid');
    if (q.from && q.to && q.from > q.to) return fail('The start is after the end');
    const needle = q.q?.trim().toLowerCase();
    const docsIn = docs.map(summary).filter((d) => (sales ? d.type.startsWith('sales') : d.type.startsWith('purchase')))
      .filter((d) => (!q.from || d.issue_date >= q.from) && (!q.to || d.issue_date <= q.to))
      .filter((d) => !needle || [d.doc_no, d.import_doc_no, d.supplier_ref, d.contact_name, d.type.endsWith('credit') ? 'credit note' : '']
        .filter(Boolean).join(' ').toLowerCase().includes(needle));
    const credit = (d: DocumentSummary) => d.type.endsWith('credit');
    const open = (d: DocumentSummary) => d.status === 'posted' || d.status === 'part_paid';
    const tabs = {
      all: (d: DocumentSummary) => (sales ? true : d.status !== 'draft' && !credit(d)),
      draft: (d: DocumentSummary) => d.status === 'draft',
      unpaid: (d: DocumentSummary) => !credit(d) && open(d) && compareMoney(d.outstanding, '0') > 0,
      overdue: (d: DocumentSummary) => !credit(d) && open(d) && !!d.due_date && d.due_date < today && !isZero(d.outstanding),
      paid: (d: DocumentSummary) => !credit(d) && d.status === 'paid',
      credit: (d: DocumentSummary) => credit(d) && (sales || d.status !== 'draft'),
      void: (d: DocumentSummary) => d.status === 'void',
    };
    const sort = q.sort ?? 'issued';
    const desc = (q.dir ?? (['issued', 'due', 'total', 'outstanding'].includes(sort) ? 'desc' : 'asc')) === 'desc';
    const numKey = (n: string | null | undefined) => (n ? n.replace(/[0-9]+$/, '').toLowerCase() + (n.match(/[0-9]+$/)?.[0] ?? '').padStart(20, '0') + n.toLowerCase() : null);
    const key = (d: DocumentSummary): string | number | null => {
      switch (sort) {
        case 'number': return numKey(sales ? d.doc_no ?? d.import_doc_no : d.supplier_ref);
        case 'contact': return d.contact_name.toLowerCase();
        case 'issued': return d.issue_date;
        case 'due': return d.due_date ?? null;
        case 'total': return (credit(d) ? -1 : 1) * Number(d.base_gross_total);
        case 'outstanding': return d.status === 'draft' || d.status === 'void' ? null : (credit(d) ? -1 : 1) * Number(d.base_outstanding);
      }
    };
    const shown = docsIn.filter(tabs[tab]).sort((a, b) => {
      const ka = key(a); const kb = key(b);
      if (ka !== kb) {
        if (ka === null) return 1;
        if (kb === null) return -1;
        const c = ka < kb ? -1 : 1;
        return desc ? -c : c;
      }
      return b.issue_date.localeCompare(a.issue_date) || b.id.localeCompare(a.id);
    });
    const off = q.offset ?? 0;
    const sum = (f: (d: DocumentSummary) => boolean) => addMoney('0', ...docsIn.filter(f).map((d) => d.base_outstanding));
    const result: DocumentList = {
      items: shown.slice(off, off + Math.min(q.limit ?? 5000, 5000)), count: shown.length,
      counts: Object.fromEntries(Object.entries(tabs).map(([k, f]) => [k, docsIn.filter(f).length])) as DocumentList['counts'],
      totals: { unpaid: sum(tabs.unpaid), overdue: sum(tabs.overdue) }, tab, sort, dir: desc ? 'desc' : 'asc',
    };
    return delay(result);
  },
  documents: (q) => {
    const today = new Date().toISOString().slice(0, 10);
    return delay(docs
      .filter((d) => !q.type || d.type === q.type)
      .filter((d) => !q.status || d.status === q.status)
      .filter((d) => !q.contactId || d.contact_id === q.contactId)
      .filter((d) => !q.overdue || ((d.status === 'posted' || d.status === 'part_paid') && !!d.due_date && d.due_date < today))
      .sort((a, b) => b.issue_date.localeCompare(a.issue_date) || b.id.localeCompare(a.id))
      .map(summary));
  },
  document: (id) => {
    const d = docs.find((x) => x.id === id);
    if (!d) return fail('That document no longer exists.');
    const { payments: _p, ...rest } = d;
    return delay(withBase(rest));
  },
  createDraft: (input) => {
    const problem = validate(input);
    if (problem) return fail(problem);
    const d = build(`d${++seq}`, input);
    docs.push(d);
    return demoDocuments.document(d.id);
  },
  replaceDraft: (id, input) => {
    const d = requireDraft(id);
    if (d instanceof Promise) return d;
    const problem = validate(input);
    if (problem) return fail(problem);
    docs = docs.map((x) => (x.id === id ? build(id, input, x) : x));
    return demoDocuments.document(id);
  },
  deleteDraft: async (id) => {
    const d = requireDraft(id);
    if (d instanceof Promise) return d;
    docs = docs.filter((x) => x.id !== id);
    await delay(null);
  },
  // With paidFrom: paid there and then from a bank, card or cash account (ledger.post_paid_document).
  checkInvoiceImport,
  invoiceMappings: () => delay([...mappings].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))),
  saveInvoiceMapping: (input) => {
    const name = input.name.trim();
    if (!name) return fail('Give the mapping a name, such as the system the file comes from');
    if (name.length > 60) return fail('The name is too long (at most 60 characters)');
    const bad = Object.keys(input.columns).find((k) => !INVOICE_FIELDS.includes(k));
    if (bad) return fail(`Unknown field ${bad}`);
    const c = input.columns;
    if (!c.number || !c.date || !c.customer_code || (!c.net && !c.gross)) {
      return fail('Choose the columns for at least the invoice number, date, account code and net (or gross) amount');
    }
    // Edited by its id (#48), it can be renamed, but not onto another mapping's name.
    const same = input.id ? mappings.find((m) => m.id === input.id) : mappings.find((m) => m.name.toLowerCase() === name.toLowerCase());
    if (input.id && !same) return fail('Unknown mapping');
    if (input.id && mappings.some((m) => m.id !== input.id && m.name.toLowerCase() === name.toLowerCase())) return fail(`There’s already a mapping called ${name}`);
    const saved: ImportMapping = { id: same?.id ?? `map${mappings.length + 1}-${Date.now()}`, name, columns: { ...c }, options: input.options ?? {}, updated_at: new Date().toISOString() };
    mappings = [...mappings.filter((m) => m !== same), saved];
    return delay({ id: saved.id });
  },
  deleteInvoiceMapping: (id) => {
    if (!mappings.some((m) => m.id === id)) return fail('Unknown mapping');
    mappings = mappings.filter((m) => m.id !== id);
    return delay(undefined);
  },
  postDocument: (id, paidFrom) => {
    const d = requireDraft(id);
    if (d instanceof Promise) return d;
    if (paidFrom && d.type !== 'purchase_bill' && d.type !== 'sales_invoice') {
      return fail('Only a bill or an invoice can be paid on the spot; credit notes are used against bills or invoices');
    }
    if (d.import_doc_no && docs.some((x) => x.type === d.type && x.doc_no === d.import_doc_no)) {
      return fail(`Number ${d.import_doc_no} is already used. Change the imported draft’s number before posting it`);
    }
    let posted: Stored = { ...d, status: 'posted', doc_no: d.import_doc_no ?? takeNumber(d.type), template_version: templates[0]?.version ?? null, journal_id: `j${seq}` };
    if (paidFrom) {
      posted = { ...posted, payments: [...posted.payments, { date: d.issue_date, amount: d.gross_total }],
        allocations: [...(posted.allocations ?? []), { document_id: 'bank', doc_no: `Paid from ${paidFrom}`, amount: d.gross_total, date: d.issue_date }] };
    }
    docs = docs.map((x) => (x.id === id ? settle(posted) : x));
    return demoDocuments.document(id);
  },
  voidDocument: (id, _date, reason) => {
    const d = docs.find((x) => x.id === id);
    if (!d) return fail('That document no longer exists.');
    if (d.status === 'draft') return fail('Drafts are deleted, not voided.');
    if (d.status === 'void') return fail('It’s already void.');
    if (d.payments.length) return fail('This invoice has payments against it. Unmatch them on the bank screen first, or raise a credit note instead.');
    if (reason.trim().length < 3) return fail('Give a reason (at least three characters).');
    docs = docs.map((x) => (x.id === id ? { ...x, status: 'void', outstanding: '0.0000', notes: x.notes, voidReason: reason } as Stored : x));
    return demoDocuments.document(id);
  },
  // ledger.credit_and_reissue: a credit note for the whole invoice, matched to it, and a new draft with its lines.
  creditAndReissue: async (id, date, reason) => {
    const d = docs.find((x) => x.id === id);
    if (!d) return fail('That document no longer exists.');
    if (d.type !== 'sales_invoice') return fail('Only a sales invoice can be credited and reissued');
    if (d.status === 'draft') return fail(`Only a posted invoice can be credited and reissued; this one is still a draft: edit it instead`);
    if (d.status === 'void') return fail(`Only a posted invoice can be credited and reissued; ${d.doc_no} is void`);
    if (d.payments.length || d.allocations?.length) return fail(`${d.doc_no} has payments or credits against it. Take those off first, then credit and reissue it.`);
    if (reason.trim().length < 3) return fail('Give a reason for crediting it');
    if (date < d.issue_date) return fail('The credit note can’t be dated before the invoice.');
    const lines = d.lines.map((l) => ({ description: l.description, quantity: l.quantity, unit_price: l.unit_price, account_code: l.account_code,
      tax_code: l.tax_code ?? undefined, product_code: l.product_code ?? undefined }));
    const base = { contact_id: d.contact_id, issue_date: date, currency: d.currency, lines };
    const cn = build(`d${++seq}`, { ...base, type: 'sales_credit', reference: d.doc_no, notes: `Credits ${d.doc_no}: ${reason.trim()}`,
      ...(d.currency !== 'EUR' ? { fx_rate: d.fx_rate } : {}) } as DocumentInput);
    const cnPosted: Stored = { ...cn, status: 'posted', doc_no: takeNumber('sales_credit'), journal_id: `j${++seq}`,
      payments: [{ date, amount: cn.gross_total }], allocations: [{ document_id: d.id, doc_no: d.doc_no ?? '', amount: cn.gross_total, date }] };
    docs.push(settle(cnPosted));
    docs = docs.map((x) => (x.id === id ? settle({ ...x, payments: [{ date, amount: d.gross_total }],
      allocations: [{ document_id: cn.id, doc_no: cnPosted.doc_no ?? 'Credit', amount: d.gross_total, date }] }) : x));
    // As save_draft: the customer's terms from the new date (#32: or from the end of its month).
    const due = dueDateFor(date, CONTACTS.find((c) => c.id === d.contact_id));
    const draft = build(`d${++seq}`, { ...base, type: 'sales_invoice', due_date: due,
      reference: d.reference ?? undefined, notes: d.notes ?? undefined } as DocumentInput);
    docs.push(draft);
    return { credit_note: await demoDocuments.document(cn.id), draft: await demoDocuments.document(draft.id) };
  },
  sendDocument: (id, input) => {
    const d = docs.find((x) => x.id === id);
    if (!d) return fail('That document no longer exists.');
    if (d.status === 'draft') return fail('Approve the invoice before sending it.');
    if (d.status === 'void') return fail('Void invoices can’t be sent.');
    const to = input.to?.length ? input.to : [CONTACTS.find((c) => c.id === d.contact_id)?.email ?? ''];
    if (!to[0]) return fail('This customer has no email address. Add one, or type it in.');
    const email: EmailMessage = { id: `e${++seq}`, status: 'queued', to, subject: input.subject ?? `Invoice ${d.doc_no}`, created_at: new Date().toISOString() };
    docs = docs.map((x) => (x.id === id ? { ...x, emails: [...(x.emails ?? []), email] } : x));
    return delay(email);
  },
  templates: () => delay(templates),
  saveTemplate: (kind, layout, settings: TemplateSettings) => {
    const current = templates.find((t) => t.kind === kind && t.is_default);
    const saved: DocumentTemplate = current
      ? { ...current, layout, settings, version: current.version + 1 }
      : { id: `t${++seq}`, kind, name: 'Standard', layout, settings, is_default: true, version: 1 };
    templates = [...templates.filter((t) => t.id !== saved.id), saved];
    return delay(saved);
  },
  // As db/047 (#43): named invoice layouts beside the default.
  createTemplate: (name, copyFrom) => {
    const n = name.trim();
    if (!n) return fail('Give the layout a name');
    if (templates.some((t) => t.kind === 'sales_invoice' && t.name.toLowerCase() === n.toLowerCase())) return fail(`There’s already a layout called ${n}`);
    const src = templates.find((t) => t.id === copyFrom) ?? templates.find((t) => t.kind === 'sales_invoice' && t.is_default);
    const t: DocumentTemplate = { id: `t${++seq}`, kind: 'sales_invoice', name: n, layout: src?.layout ?? 'classic',
      settings: structuredClone(src?.settings ?? {}), is_default: false, version: 1 };
    templates = [...templates, t];
    return delay(t);
  },
  updateTemplate: (id, patch) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return fail('There’s no such layout');
    const name = patch.name?.trim();
    if (name && templates.some((x) => x.id !== id && x.kind === t.kind && x.name.toLowerCase() === name.toLowerCase())) return fail('That already exists');
    const designChanged = (patch.layout && patch.layout !== t.layout) || (patch.settings && JSON.stringify(patch.settings) !== JSON.stringify(t.settings));
    const saved = { ...t, ...(name ? { name } : {}), ...(patch.layout ? { layout: patch.layout } : {}), ...(patch.settings ? { settings: patch.settings } : {}),
      version: designChanged ? t.version + 1 : t.version };
    templates = templates.map((x) => (x.id === id ? saved : x));
    return delay(saved);
  },
  deleteTemplate: (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return fail('There’s no such layout');
    if (t.is_default) return fail('The default layout can’t be deleted');
    templates = templates.filter((x) => x.id !== id);
    CONTACTS.forEach((c) => { if (c.invoice_template_id === id) c.invoice_template_id = null; });
    return delay(undefined);
  },
  pdf: () => Promise.resolve(null),

  // As ledger.contact_transactions (db/033): documents and their payments, oldest first, with a running balance (#11).
  contactTransactions: (id, q = {}) => {
    const c = CONTACTS.find((x) => x.id === id);
    if (!c) return fail('That customer or supplier isn’t in these books');
    const side = q.side ?? (c.is_customer ? 'customer' : 'supplier');
    const to = q.to ?? DEMO_TODAY;
    if (q.from && q.from > to) return fail('The start is after the end');
    const sign = side === 'customer' ? 1 : -1;
    const kinds = side === 'customer' ? ['sales_invoice', 'sales_credit'] : ['purchase_bill', 'purchase_credit'];
    type Move = { date: string; no: string; source: string; description: string; amount: number; doc: Stored | null; cash?: CashDocument };
    const moves: Move[] = [];
    for (const d of docs.filter((x) => x.contact_id === id && kinds.includes(x.type) && x.status !== 'draft' && x.doc_no)) {
      const gross = Number(baseOf(d.gross_total, d.fx_rate));
      const credit = d.type.endsWith('credit');
      // An invoice is a debit to debtors; a bill a credit to creditors; credit notes the other way.
      const amount = (credit ? -1 : 1) * sign * gross;
      moves.push({ date: d.issue_date, no: d.doc_no!, source: d.type, description: `${d.doc_no} ${c.name}`, amount, doc: d });
      for (const p of d.payments.filter((x) => !x.cash)) {
        const paid = Number(baseOf(p.amount, d.fx_rate));
        moves.push({ date: p.date, no: `${d.doc_no}-P`, source: side === 'customer' ? 'receipt' : 'payment',
          description: `${side === 'customer' ? 'Receipt from' : 'Payment to'} ${c.name}`, amount: -amount / gross * paid, doc: null });
      }
      if (d.status === 'void') moves.push({ date: d.issue_date, no: `${d.doc_no}-V`, source: 'reversal', description: `Void ${d.doc_no}`, amount: -amount, doc: null });
    }
    for (const x of cash.filter((k) => k.contact_id === id && (k.type === (side === 'customer' ? 'receipt' : 'payment') || k.is_refund))) {
      const out = x.is_refund ? 1 : -1; // a refund to the customer is a debit, like an invoice
      moves.push({ date: x.date, no: x.number, source: x.type,
        description: x.is_refund ? `Refund ${x.number} of ${x.refunds}` : `${x.type === 'receipt' ? 'Receipt' : 'Payment'} ${x.number}${x.reference ? ` (${x.reference})` : ''}`,
        amount: out * sign * Number(x.amount), doc: null, cash: x });
      if (x.status === 'void' && x.voided) moves.push({ date: x.voided.date ?? x.date, no: `${x.number}-V`, source: 'reversal', description: x.voided.reason ?? `Undo ${x.number}`, amount: -out * sign * Number(x.amount), doc: null });
    }
    moves.sort((a, b) => a.date.localeCompare(b.date) || a.no.localeCompare(b.no));
    const fix = (n: number) => (Math.round(n * 100) / 100).toFixed(4);
    const opening = sign * moves.filter((m) => q.from && m.date < q.from).reduce((t, m) => t + m.amount, 0);
    let running = opening;
    const lines = moves.filter((m) => (!q.from || m.date >= q.from) && m.date <= to).map((m, i) => {
      running += sign * m.amount;
      return {
        date: m.date, journal_id: `jt${i}`, journal_no: `J-${m.no}`, source: m.source, description: m.description,
        document: m.doc ? { id: m.doc.id, type: m.doc.type, number: m.doc.doc_no ?? null, reference: m.doc.supplier_ref ?? m.doc.reference ?? null,
          due_date: m.doc.due_date ?? null, outstanding: m.doc.status === 'void' ? null : m.doc.outstanding }
          : m.cash ? { id: m.cash.id, type: m.cash.type, number: m.cash.number, reference: m.cash.reference ?? null, due_date: null, outstanding: null } : null,
        debit: fix(Math.max(m.amount, 0)), credit: fix(Math.max(-m.amount, 0)), balance: fix(running),
      };
    });
    const now = sign * moves.reduce((t, m) => t + m.amount, 0);
    const overdue = docs.filter((d) => d.contact_id === id && kinds.includes(d.type) && !d.type.endsWith('credit')
      && (d.status === 'posted' || d.status === 'part_paid') && (d.due_date ?? d.issue_date) < DEMO_TODAY)
      .reduce((t, d) => t + Number(baseOf(d.outstanding, d.fx_rate)), 0);
    return delay({ contact_id: id, name: c.name, side, from: q.from ?? null, to, opening_balance: fix(opening), closing_balance: fix(running),
      current_balance: fix(now), overdue: fix(overdue), lines });
  },
  contact: (id) => {
    const c = CONTACTS.find((x) => x.id === id);
    if (!c) return fail('That contact no longer exists.');
    return demoDocuments.contacts(c.is_customer ? 'customer' : 'supplier').then((list) => list.find((x) => x.id === id)!);
  },
  // As ledger.record_cash (db/035): into a bank account, allocated as given, the rest on account (#26).
  recordReceipt: (input) => recordCash('receipt', input),
  recordPayment: (input) => recordCash('payment', input),
  onAccount: (contactId) => delay(cash.filter((x) => x.contact_id === contactId && compareMoney(onAccountOf(x), '0') > 0)
    .map((x) => ({ ...x, on_account: onAccountOf(x) }))),
  cashDocument: (id) => {
    const c = cash.find((x) => x.id === id);
    return c ? delay({ ...c, on_account: onAccountOf(c) }) : fail('That receipt or payment no longer exists.');
  },
  // As ledger.reallocate (db/038): the whole new set; what comes off needs a reason and is held on account.
  reallocateCash: async (id, allocations, reason) => {
    const c = cash.find((x) => x.id === id);
    if (!c) return fail('That receipt or payment no longer exists.');
    if (c.status === 'void') return fail(`${c.number} is void; nothing can be allocated from it`);
    const want = new Map<string, string>();
    for (const a of allocations) {
      if (compareMoney(a.amount || '0', '0') < 0) return fail('Amounts are positive');
      want.set(a.document_id, addMoney(want.get(a.document_id) ?? '0', a.amount || '0'));
    }
    const had = new Map(c.allocations.filter((a) => a.type !== 'payment' && a.type !== 'receipt').map((a) => [a.document_id, a.amount]));
    const ids = new Set([...had.keys(), ...want.keys()]);
    const changes = [...ids].map((d) => ({ d, change: addMoney(want.get(d) ?? '0', negate(had.get(d) ?? '0')) })).filter((x) => !isZero(x.change));
    if (changes.some((x) => compareMoney(x.change, '0') < 0) && (reason ?? '').trim().length < 3) return fail('Give a reason for taking an allocation off');
    const before = structuredClone({ c, docs });
    for (const x of changes.filter((y) => compareMoney(y.change, '0') < 0)) takeOffCash(c, x.d, negate(x.change), DEMO_DAY);
    const adds = changes.filter((y) => compareMoney(y.change, '0') > 0).map((y) => ({ document_id: y.d, amount: y.change }));
    try {
      if (adds.length) await allocateCash(c, adds);
    } catch (e) {
      Object.assign(c, before.c);
      docs = before.docs;
      throw e;
    }
    return demoDocuments.cashDocument(id);
  },
  // As ledger.undo_cash (db/038): not while in the bank or refunded; allocations off, then void.
  undoCash: async (id, date, reason) => {
    const c = cash.find((x) => x.id === id);
    if (!c) return fail('That receipt or payment no longer exists.');
    if (c.status === 'void') return fail(`${c.number} is already void`);
    if (c.in_bank) return fail(`${c.number} is matched to a bank statement line. Unmatch it on the bank reconciliation first, then undo it.`);
    const refund = c.allocations.find((a) => a.type === 'payment' || a.type === 'receipt');
    if (refund) return fail(`Part of ${c.number} was refunded by ${refund.number}. Undo the refund first.`);
    if (reason.trim().length < 3) return fail('Give a reason for undoing it');
    if (date < c.date) return fail(`It can’t be undone before the ${c.is_refund ? 'refund' : c.type} was dated (${c.date})`);
    for (const a of [...c.allocations]) takeOffCash(c, a.document_id, a.amount, date);
    if (c.is_refund) {
      const from = cash.find((x) => x.number === c.refunds);
      if (from) {
        from.allocations = from.allocations.filter((a) => a.document_id !== c.id);
        from.allocated = addMoney(from.allocated, negate(c.amount));
        from.on_account = onAccountOf(from);
      }
    }
    c.status = 'void';
    c.voided = { date, reason: `Undo ${c.number}: ${reason.trim()}` };
    c.on_account = '0.0000';
    return demoDocuments.cashDocument(id);
  },
  // As ledger.refund_on_account (db/038): a payment out of a bank account, matched to the receipt.
  refundOnAccount: async (id, input) => {
    const from = cash.find((x) => x.id === id);
    if (!from || from.is_refund) return fail('Only money held on account (a receipt, payment or credit note) can be refunded');
    if (from.status === 'void') return fail(`${from.number} is void; there’s nothing to refund`);
    const left = onAccountOf(from);
    if (!/^\d+(\.\d{1,2})?$/.test(String(input.amount).trim()) || Number(input.amount) <= 0) return fail('Enter the amount to refund, more than nothing');
    const amount = Number(input.amount).toFixed(4);
    if (compareMoney(amount, left) > 0) return fail(`${from.number} has only ${Number(left).toFixed(2)} on account to refund`);
    const date = input.date ?? DEMO_TODAY;
    if (date < from.date) return fail(`The refund can’t be dated before ${from.number} (${from.date})`);
    const bank = demoStore.accounts.find((a) => a.code === input.bank_account && a.control === 'bank');
    if (!bank) return fail('Choose the bank account the refund is paid from');
    const type = from.type === 'receipt' ? 'payment' : 'receipt';
    const r: CashDocument = {
      id: `cash${++cashSeq}`, type, number: `${type === 'receipt' ? 'RCT' : 'PAY'}-${String(cashSeq).padStart(6, '0')}`,
      contact_id: from.contact_id, contact_name: from.contact_name, date, amount, currency: 'EUR',
      bank_account: { code: bank.code, name: bank.name }, reference: input.reference ?? null, notes: `Refund of ${from.number}`,
      allocated: '0.0000', on_account: '0.0000', allocations: [], in_bank: false, status: 'posted', is_refund: true, refunds: from.number, voided: null,
    };
    cash.push(r);
    from.allocations.push({ document_id: r.id, number: r.number, type, amount });
    from.allocated = addMoney(from.allocated, amount);
    from.on_account = onAccountOf(from);
    return delay(r);
  },
  // As ledger.import_contacts (db/034), in brief: match by the package's id, code, then name; new ones take the
  // reference as their code when it's free; Irish VAT numbers get their IE (#16).
  importContacts: async (req) => {
    const role = req.role;
    const seen = new Map<string, number>();
    const codes = new Set<string>();
    const out: Awaited<ReturnType<LedgerSource['importContacts']>>['rows'] = [];
    let created = 0, updated = 0, skipped = 0;
    for (const r of req.rows) {
      const name = r.name?.trim();
      if (!name) { out.push({ row: r.row, name: null, action: 'skip', code: null, contact_id: null, notes: ['No company name'] }); skipped++; continue; }
      if (r.unique_id && seen.has(r.unique_id)) {
        out.push({ row: r.row, name, action: 'skip', code: null, contact_id: null, notes: [`The same contact as row ${seen.get(r.unique_id)}`] }); skipped++; continue;
      }
      if (r.unique_id) seen.set(r.unique_id, r.row);
      const notes: string[] = [];
      const ref = r.reference?.trim().toUpperCase() || null;
      const mine = (c: Contact) => (role === 'customer' ? c.is_customer : c.is_supplier);
      let have = (r.unique_id && CONTACTS.find((c) => importedIds.get(r.unique_id!) === c.id)) || undefined;
      if (!have && ref) { have = CONTACTS.find((c) => mine(c) && c.code?.toUpperCase() === ref); if (have) notes.push(`Matched by code ${ref}`); }
      if (!have) { have = CONTACTS.find((c) => mine(c) && c.name.trim().toLowerCase() === name.toLowerCase()); if (have) notes.push('Matched by name'); }
      const country = (r.address?.country || 'IE').toUpperCase();
      const rawVat = r.vat_number?.replace(/[\s.-]/g, '').toUpperCase() || null;
      const vat = rawVat && !/^[A-Z]{2}/.test(rawVat) ? `${country === 'GR' ? 'EL' : country}${rawVat}` : rawVat;
      if (vat && vat !== rawVat) notes.push(`VAT number ${vat}`);
      let email = r.email?.trim() || null;
      if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { notes.push(`"${email}" isn’t an email address; left out`); email = null; }
      let code = ref;
      if (have?.code && ref && have.code.toUpperCase() !== ref) notes.push(`Keeps its code ${have.code} (${ref} in the file)`);
      if (have) code = have.code ?? ref;
      if (code && !have?.code) {
        const other = CONTACTS.find((c) => c.code?.toUpperCase() === code && c.id !== have?.id);
        if (other || codes.has(code)) { notes.push(`Code ${code} is already ${other?.name ?? 'another row'}’s`); code = null; }
      }
      if (code) codes.add(code);
      const terms = r.terms && /^\d{1,3}$/.test(r.terms) && Number(r.terms) <= 365 ? Number(r.terms) : undefined;
      const fields = { name, vat_number: vat ?? undefined, country, email: email ?? undefined, payment_terms_days: terms,
        address: { line1: r.address?.line1 ?? undefined, line2: r.address?.line2 ?? undefined, town: r.address?.town ?? undefined,
          county: r.address?.county ?? undefined, postcode: r.address?.postcode ?? undefined, country } };
      let id = have?.id ?? null;
      if (req.apply) {
        if (have) {
          await demoDocuments.updateContact(have.id, { ...fields, is_customer: have.is_customer || role === 'customer', is_supplier: have.is_supplier || role === 'supplier', ...(have.code ? {} : { code: code ?? undefined }) } as ContactInput);
          if (r.active?.toLowerCase() === 'no') have.is_active = false;
        } else {
          const c = await demoDocuments.createContact({ ...fields, is_customer: role === 'customer', is_supplier: role === 'supplier', code: code ?? undefined, currency: r.currency ?? undefined } as ContactInput);
          if (r.active?.toLowerCase() === 'no') CONTACTS.find((x) => x.id === c.id)!.is_active = false;
          id = c.id; code = c.code ?? null;
        }
        if (r.unique_id && id) importedIds.set(r.unique_id, id);
      }
      if (have) updated++; else created++;
      out.push({ row: r.row, name, action: have ? 'update' : 'new', code, contact_id: id, notes });
    }
    return delay({ rows: out, new: created, update: updated, skip: skipped, batch_id: req.apply ? `b${Date.now()}` : null });
  },
  createContact: (input) => {
    const typed = input.code?.trim().toUpperCase() || null;
    const problem = contactProblem({ ...input, code: typed });
    if (problem) return fail(problem);
    const people = withPeople(input);
    if ('problem' in people) return fail(people.problem);
    input = { ...input, ...people.fields };
    const code = typed ?? (demoContactCodes.mode === 'auto' ? nextContactCode(!input.is_customer && !!input.is_supplier) : null);
    const c: Contact = { id: `c${Date.now()}`, is_customer: !!input.is_customer, is_supplier: !!input.is_supplier, is_active: true,
      balance: '0', country: 'IE', payment_terms_days: 30, ...input, name: input.name.trim(), code };
    CONTACTS.push(c);
    return demoDocuments.contact(c.id);
  },
  updateContact: (id, input) => {
    const c = CONTACTS.find((x) => x.id === id);
    if (!c) return fail('That contact no longer exists.');
    const code = input.code === undefined || input.code === null ? undefined : input.code.trim().toUpperCase() || null;
    const problem = contactProblem({ ...c, ...input, code: code === undefined ? c.code : code }, id);
    if (problem) return fail(problem);
    const people = withPeople(input);
    if ('problem' in people) return fail(people.problem);
    input = { ...input, ...people.fields };
    if (input.invoice_template_id === ZERO_ID) input = { ...input, invoice_template_id: null }; // back to the default (#43)
    Object.assign(c, Object.fromEntries(Object.entries({ ...input, code }).filter(([, v]) => v !== undefined)));
    return demoDocuments.contact(id);
  },
  // A credit note is set against the same customer's invoices; never more than either has left.
  allocateCredit: (id, allocations) => {
    const held = cash.find((x) => x.id === id);
    if (held) return allocateCash(held, allocations).then(() => demoDocuments.document(docs.find((d) => d.allocations?.some((a) => a.document_id === id))?.id ?? allocations[0]!.document_id));
    const credit = docs.find((x) => x.id === id);
    if (!credit || (credit.type !== 'sales_credit' && credit.type !== 'purchase_credit') || (credit.status !== 'posted' && credit.status !== 'part_paid')) {
      return fail('Only a posted credit note with something left can be allocated.');
    }
    const against = credit.type === 'sales_credit' ? 'sales_invoice' : 'purchase_bill';
    let left = credit.outstanding;
    for (const a of allocations) {
      const inv = docs.find((x) => x.id === a.document_id);
      if (!inv || inv.type !== against || inv.contact_id !== credit.contact_id) {
        return fail(against === 'sales_invoice' ? 'Credits can only go against the same customer’s invoices.' : 'Credits can only go against the same supplier’s bills.');
      }
      if (compareMoney(a.amount, '0') <= 0) return fail('Amounts must be more than zero.');
      if (compareMoney(a.amount, inv.outstanding) > 0) return fail(`${inv.doc_no} only has ${inv.outstanding.replace(/(\.\d\d)00$/, '$1')} left to pay.`);
      left = addMoney(left, negate(a.amount));
    }
    if (compareMoney(left, '0') < 0) return fail('That’s more than the credit note has left.');
    const today = DEMO_DAY;
    for (const a of allocations) {
      docs = docs.map((x) => (x.id === a.document_id
        ? settle({ ...x, payments: [...x.payments, { date: today, amount: a.amount }], allocations: [...(x.allocations ?? []), { document_id: id, doc_no: credit.doc_no ?? 'Credit', amount: a.amount, date: today }] })
        : x));
    }
    docs = docs.map((x) => (x.id === id
      ? settle({ ...x, payments: [...x.payments, ...allocations.map((a) => ({ date: today, amount: a.amount }))],
        allocations: [...(x.allocations ?? []), ...allocations.map((a) => ({ document_id: a.document_id, doc_no: docs.find((d) => d.id === a.document_id)?.doc_no ?? '', amount: a.amount, date: today }))] })
      : x));
    return demoDocuments.document(id);
  },

  // Figures for the report screens: year to date for a fire and security installer.
  trialBalance: () => delay(TRIAL_BALANCE),
  balanceSheet: () => delay(BALANCE_SHEET),
  agedCreditors: (asAt) => {
    const rows = new Map<string, AgedRow>();
    for (const d of docs.filter((x) => x.type === 'purchase_bill' && (x.status === 'posted' || x.status === 'part_paid') && x.issue_date <= asAt)) {
      const c = CONTACTS.find((x) => x.id === d.contact_id)!;
      const r = rows.get(c.id) ?? { contact_id: c.id, contact_name: c.name, current: '0', d30: '0', d60: '0', d90: '0', older: '0', total: '0' };
      const late = d.due_date ? Math.round((Date.parse(asAt) - Date.parse(d.due_date)) / 86_400_000) : 0;
      const k = late <= 0 ? 'current' : late <= 30 ? 'd30' : late <= 60 ? 'd60' : late <= 90 ? 'd90' : 'older';
      // In euro, at the bill's rate (as ledger.aged_by_contact).
      const base = d.currency === 'EUR' ? d.outstanding : mulMoney(d.outstanding, Number(d.fx_rate ?? '1').toFixed(6));
      r[k] = addMoney(r[k], base);
      r.total = addMoney(r.total, base);
      rows.set(c.id, r);
    }
    return delay([...rows.values()].sort((a, b) => compareMoney(b.total, a.total)));
  },
};

type AgedRow = { contact_id: string; contact_name: string; current: string; d30: string; d60: string; d90: string; older: string; total: string };
const DEMO_DAY = '2026-10-11';

// ---- Short codes (#20), as the database gives them: typed, or a prefix and a number when automatic.
export const demoContactCodes = { mode: 'auto' as 'auto' | 'manual', customer_prefix: 'C', supplier_prefix: 'S', digits: 4 };
const codeNext = { customer: 1, supplier: 1 };
const CODE_OK = /^[A-Z0-9][A-Z0-9._/&-]{0,19}$/;
export function nextContactCode(supplier: boolean): string {
  for (;;) {
    const k = supplier ? 'supplier' : 'customer';
    const code = `${supplier ? demoContactCodes.supplier_prefix : demoContactCodes.customer_prefix}${String(codeNext[k]++).padStart(demoContactCodes.digits, '0')}`;
    if (!CONTACTS.some((c) => c.code?.toUpperCase() === code)) return code;
  }
}
export function demoContactsWithoutCode(): Contact[] {
  return CONTACTS.filter((c) => !c.code);
}
for (const c of CONTACTS) c.code ??= nextContactCode(Boolean(c.is_supplier && !c.is_customer));

function contactProblem(c: { name: string; email?: string | null; vat_number?: string | null; code?: string | null }, id?: string): string | null {
  if (!c.name.trim()) return 'Give the contact a name.';
  if (c.code) {
    if (!CODE_OK.test(c.code)) return 'A code is up to 20 letters and digits (with . _ / & - inside), such as SWORDS01';
    const other = CONTACTS.find((x) => x.id !== id && x.code?.toUpperCase() === c.code!.toUpperCase());
    if (other) return `Code ${c.code.toUpperCase()} is already used by ${other.name}`;
  }
  if (c.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(c.email)) return 'That email address doesn’t look right.';
  if (CONTACTS.some((x) => x.id !== id && x.name.toLowerCase() === c.name.trim().toLowerCase())) return `There’s already a contact called ${c.name.trim()}.`;
  return null;
}

const tb = (code: string, name: string, cls: AccountBalance['class'], debit: string, credit: string): AccountBalance =>
  ({ code, name, class: cls, debit, credit, balance: addMoney(debit, negate(credit)) });
export const TRIAL_BALANCE: AccountBalance[] = [
  tb('0030', 'Equipment and vehicles', 'asset', '28500.00', '0'),
  tb('1100', 'Debtors control', 'asset', '12486.50', '0'),
  tb('1200', 'AIB Current', 'asset', '16950.12', '0'),
  tb('1210', 'AIB Deposit', 'asset', '1300.00', '0'),
  tb('1230', 'Petty cash', 'asset', '170.24', '0'),
  tb('2100', 'Creditors control', 'liability', '0', '4112.80'),
  tb('2200', 'VAT on sales', 'liability', '0', '3926.75'),
  tb('2210', 'PAYE / PRSI / USC', 'liability', '0', '2850.00'),
  tb('3000', 'Capital introduced', 'equity', '0', '100.00'),
  tb('3200', 'Retained earnings', 'equity', '0', '1795.61'),
  tb('4000', 'Sales — goods', 'income', '0', '158220.00'),
  tb('4010', 'Sales — services', 'income', '0', '56160.00'),
  tb('5000', 'Materials purchased', 'expense', '72412.40', '0'),
  tb('5100', 'Subcontractors', 'expense', '24100.00', '0'),
  tb('6000', 'Wages and salaries', 'expense', '48600.00', '0'),
  tb('7000', 'Rent and rates', 'expense', '14400.00', '0'),
  tb('7200', 'Motor expenses', 'expense', '8245.90', '0'),
];
const bs = (section: AccountBalance['section'], code: string | null, name: string, balance: string): AccountBalance => ({ section, code, name, balance });
const BALANCE_SHEET: AccountBalance[] = [
  bs('assets', '0030', 'Equipment and vehicles', '28500.00'),
  bs('assets', '1100', 'Debtors control', '12486.50'),
  bs('assets', '1200', 'AIB Current', '16950.12'),
  bs('assets', '1210', 'AIB Deposit', '1300.00'),
  bs('assets', '1230', 'Petty cash', '170.24'),
  bs('liabilities', '2100', 'Creditors control', '4112.80'),
  bs('liabilities', '2200', 'VAT on sales', '3926.75'),
  bs('liabilities', '2210', 'PAYE / PRSI / USC', '2850.00'),
  bs('equity', '3000', 'Capital introduced', '100.00'),
  bs('equity', '3200', 'Retained earnings', '1795.61'),
  bs('equity', null, 'Profit not yet closed', '46621.70'),
];
